import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import { Users as UsersIcon, Plus, Edit2, X, Key } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { apiError } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';

const ROLES = ['admin', 'manager', 'sales_manager', 'production_manager'];
const emptyForm = { username: '', password: '', full_name: '', role: 'sales_manager' };
const emptyPwd = { old_password: '', new_password: '' };

export default function Users() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [pwdModal, setPwdModal] = useState(false);
  const [pwdForm, setPwdForm] = useState(emptyPwd);

  const load = () => {
    setLoading(true);
    authAPI.listUsers()
      .then(r => setUsers(r.data))
      .catch(() => toast.error(t('error')))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openCreate = () => { setEditUser(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = u => { setEditUser(u); setForm({ username: u.username, password: '', full_name: u.full_name, role: u.role, is_active: u.is_active }); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditUser(null); setForm(emptyForm); };

  const handleSave = async () => {
    if (!form.username || !form.full_name) return toast.error(t('fill_required'));
    if (!editUser && !form.password) return toast.error(t('password_required'));
    setSaving(true);
    try {
      if (editUser) {
        const data = { full_name: form.full_name, role: form.role, is_active: form.is_active !== false };
        if (form.password) data.password = form.password;
        await authAPI.updateUser(editUser.id, data);
        toast.success(t('saved'));
      } else {
        await authAPI.createUser(form);
        toast.success(t('saved'));
      }
      closeModal(); load();
    } catch (e) { toast.error(apiError(e, t)); }
    finally { setSaving(false); }
  };

  const handleChangePwd = async () => {
    if (!pwdForm.old_password || !pwdForm.new_password) return toast.error(t('fill_required'));
    setSaving(true);
    try {
      await authAPI.changePassword(pwdForm);
      toast.success(t('saved')); setPwdModal(false); setPwdForm(emptyPwd);
    } catch (e) { toast.error(apiError(e, t)); }
    finally { setSaving(false); }
  };

  const roleBadge = role => {
    const map = { admin: 'badge-red', manager: 'badge-orange', sales_manager: 'badge-blue', production_manager: 'badge-green' };
    return <span className={`badge ${map[role] || ''}`}>{t('role_' + role)}</span>;
  };

  return (
    <div className="space-y-5">
      <SectionHero title={t('users')} subtitle={<>{users.length} {t('users')}</>}>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={() => { setPwdForm(emptyPwd); setPwdModal(true); }}>
            <Key size={15} /> {t('change_password')}
          </button>
          {isAdmin && <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> {t('add_user')}</button>}
        </div>
      </SectionHero>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>{t('full_name')}</th>
                <th>{t('username')}</th>
                <th>{t('role')}</th>
                <th>{t('status')}</th>
                {isAdmin && <th>{t('actions')}</th>}
              </tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{u.full_name}</td>
                    <td style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{u.username}</td>
                    <td>{roleBadge(u.role)}</td>
                    <td>{u.is_active ? <span className="badge badge-green">{t('active')}</span> : <span className="badge badge-red">{t('inactive')}</span>}</td>
                    {isAdmin && (
                      <td>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(u)}><Edit2 size={13} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{editUser ? t('edit_user') : t('add_user')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={closeModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">{t('full_name')} *</label><input className="input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">{t('username')} *</label><input className="input" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} disabled={!!editUser} /></div>
                <div className="form-group"><label className="form-label">{editUser ? t('new_password_optional') : t('password') + ' *'}</label><input className="input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} /></div>
              </div>
              {editUser && editUser.id !== user?.id && (
                <div className="checkbox-wrap mb-3" onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}>
                  <input type="checkbox" checked={form.is_active !== false} readOnly /><span>{t('active')}</span>
                </div>
              )}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('role')}</label>
                <select className="select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  {ROLES.map(r => <option key={r} value={r}>{t('role_' + r)}</option>)}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('loading') : t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {pwdModal && (
        <div className="modal-overlay" onClick={() => setPwdModal(false)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{t('change_password')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setPwdModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group"><label className="form-label">{t('old_password')}</label><input className="input" type="password" value={pwdForm.old_password} onChange={e => setPwdForm(f => ({ ...f, old_password: e.target.value }))} /></div>
              <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{t('new_password')}</label><input className="input" type="password" value={pwdForm.new_password} onChange={e => setPwdForm(f => ({ ...f, new_password: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setPwdModal(false)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleChangePwd} disabled={saving}>{saving ? t('loading') : t('save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

